import React, { useState } from 'react';
import axios from 'axios';
import { Mail, MapPin, ArrowRight, Send, AlertCircle } from 'lucide-react';
import { contactContent } from '../data/mock';
import { useLanguage } from '../context/LanguageContext';
import useDocumentTitle from '../hooks/useDocumentTitle';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ContactPage = () => {
  const { language, t, pick } = useLanguage();
  useDocumentTitle(language === 'fr' ? 'Contact' : 'Contact');
  const [formData, setFormData] = useState({ name: '', email: '', company: '', subject: '', message: '' });
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSubmitting(true);
    try {
      await axios.post(`${API_URL}/contact`, formData);
      setSubmitted(true);
      setFormData({ name: '', email: '', company: '', subject: '', message: '' });
      setTimeout(() => setSubmitted(false), 5000);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setError(
        Array.isArray(detail)
          ? detail.map((d) => d.msg).join(', ')
          : detail || t('contact.errorGeneric')
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="pt-20">
      <section className="section-padding-lg bg-[#1a1c1b] relative overflow-hidden">
        <div className="absolute inset-0 opacity-5">
          <div className="absolute -top-20 -right-20 w-[500px] h-[500px] border border-[#5B7A8A] rounded-full"></div>
        </div>
        <div className="container-swg relative z-10">
          <div className="max-w-4xl">
            <span className="caption text-[#5B7A8A] block mb-4">{pick(contactContent.title)}</span>
            <h1 className="heading-1 text-white mb-8">{t('contact.title')}</h1>
            <div className="accent-line mb-8"></div>
            <p className="body-large max-w-2xl">{t('contact.subtitle')}</p>
          </div>
        </div>
      </section>

      <section className="section-padding-lg bg-[#302f2c]/30 border-t border-[rgba(91,122,138,0.3)]">
        <div className="container-swg">
          <div className="grid lg:grid-cols-5 gap-16">
            <div className="lg:col-span-2">
              <span className="caption text-[#5B7A8A] block mb-4">{t('contact.sectionTitle')}</span>
              <h2 className="heading-3 text-white mb-8">{t('contact.directly')}</h2>

              <div className="space-y-6">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-[#3d5a6a]/50 rounded-xl flex items-center justify-center flex-shrink-0">
                    <Mail className="w-5 h-5 text-[#5B7A8A]" />
                  </div>
                  <div>
                    <p className="text-white font-medium mb-1">{t('contact.emailLabel')}</p>
                    <a href={`mailto:${contactContent.email}`} className="text-[#5B7A8A] hover:underline">{contactContent.email}</a>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-[#3d5a6a]/50 rounded-xl flex items-center justify-center flex-shrink-0">
                    <MapPin className="w-5 h-5 text-[#5B7A8A]" />
                  </div>
                  <div>
                    <p className="text-white font-medium mb-1">{t('contact.officesLabel')}</p>
                    <p className="text-[#9AACB8]">{pick(contactContent.offices)}</p>
                  </div>
                </div>
              </div>

              <div className="mt-12 p-6 swg-card rounded-xl">
                <p className="text-[#9AACB8] text-sm mb-4">{t('contact.careersIntro')}</p>
                <a href="mailto:careers@smartworld.group" className="text-[#5B7A8A] hover:underline">careers@smartworld.group</a>
              </div>
            </div>

            <div className="lg:col-span-3">
              <div className="swg-card rounded-2xl p-8 lg:p-10">
                <h3 className="heading-4 text-white mb-6">{t('contact.formTitle')}</h3>

                {submitted ? (
                  <div className="text-center py-12" data-testid="contact-success">
                    <div className="w-16 h-16 bg-[#5B7A8A] rounded-full flex items-center justify-center mx-auto mb-6">
                      <Send className="w-8 h-8 text-[#1a1c1b]" />
                    </div>
                    <h4 className="text-white font-semibold text-xl mb-2">{t('contact.successTitle')}</h4>
                    <p className="text-[#9AACB8]">{t('contact.successSub')}</p>
                  </div>
                ) : (
                  <form onSubmit={handleSubmit} className="space-y-6" data-testid="contact-form">
                    {error && (
                      <div className="flex items-start gap-3 p-4 rounded-lg border border-red-500/40 bg-red-500/10 text-red-200 text-sm" data-testid="contact-error">
                        <AlertCircle className="w-5 h-5 mt-0.5 flex-shrink-0" />
                        <span>{error}</span>
                      </div>
                    )}
                    <div className="grid md:grid-cols-2 gap-6">
                      <div>
                        <label className="block text-[#9AACB8] text-sm mb-2">{t('contact.name')} *</label>
                        <input type="text" name="name" value={formData.name} onChange={handleChange} required className="w-full bg-[#1a1c1b] border border-[rgba(91,122,138,0.5)] rounded-lg px-4 py-3 text-white placeholder-[#9AACB8] focus:outline-none focus:border-[#5B7A8A] transition-colors" placeholder={t('contact.namePh')} data-testid="contact-input-name" />
                      </div>
                      <div>
                        <label className="block text-[#9AACB8] text-sm mb-2">{t('contact.email')} *</label>
                        <input type="email" name="email" value={formData.email} onChange={handleChange} required className="w-full bg-[#1a1c1b] border border-[rgba(91,122,138,0.5)] rounded-lg px-4 py-3 text-white placeholder-[#9AACB8] focus:outline-none focus:border-[#5B7A8A] transition-colors" placeholder={t('contact.emailPh')} data-testid="contact-input-email" />
                      </div>
                    </div>

                    <div className="grid md:grid-cols-2 gap-6">
                      <div>
                        <label className="block text-[#9AACB8] text-sm mb-2">{t('contact.company')}</label>
                        <input type="text" name="company" value={formData.company} onChange={handleChange} className="w-full bg-[#1a1c1b] border border-[rgba(91,122,138,0.5)] rounded-lg px-4 py-3 text-white placeholder-[#9AACB8] focus:outline-none focus:border-[#5B7A8A] transition-colors" placeholder={t('contact.companyPh')} data-testid="contact-input-company" />
                      </div>
                      <div>
                        <label className="block text-[#9AACB8] text-sm mb-2">{t('contact.subject')} *</label>
                        <select name="subject" value={formData.subject} onChange={handleChange} required className="w-full bg-[#1a1c1b] border border-[rgba(91,122,138,0.5)] rounded-lg px-4 py-3 text-white focus:outline-none focus:border-[#5B7A8A] transition-colors" data-testid="contact-input-subject">
                          <option value="" className="bg-[#1a1c1b]">{t('contact.selectSubject')}</option>
                          <option value="partnership" className="bg-[#1a1c1b]">{t('contact.partnership')}</option>
                          <option value="investment" className="bg-[#1a1c1b]">{t('contact.investment')}</option>
                          <option value="media" className="bg-[#1a1c1b]">{t('contact.media')}</option>
                          <option value="general" className="bg-[#1a1c1b]">{t('contact.general')}</option>
                        </select>
                      </div>
                    </div>

                    <div>
                      <label className="block text-[#9AACB8] text-sm mb-2">{t('contact.message')} *</label>
                      <textarea name="message" value={formData.message} onChange={handleChange} required rows={5} className="w-full bg-[#1a1c1b] border border-[rgba(91,122,138,0.5)] rounded-lg px-4 py-3 text-white placeholder-[#9AACB8] focus:outline-none focus:border-[#5B7A8A] transition-colors resize-none" placeholder={t('contact.messagePh')} data-testid="contact-input-message" />
                    </div>

                    <button type="submit" disabled={submitting} className="btn-primary w-full md:w-auto disabled:opacity-60 disabled:cursor-not-allowed" data-testid="contact-submit-button">
                      {submitting ? t('contact.sending') : t('contact.send')}
                      <ArrowRight className="w-4 h-4 ml-2" />
                    </button>
                  </form>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ContactPage;
